import React, { useState, useEffect } from "react";
import Login from "./pages/Login";
import Register from "./pages/Register";
import VendorRegister from "./pages/VendorRegister";
import Dashboard from "./pages/Dashboard";
import VendorDashboard from "./pages/VendorDashboard";
import Premium from "./pages/Premium";
import AdminDashboard from "./pages/AdminDashboard";
import OtpVerification from "./pages/OtpVerification";
import UserIdCard from "./pages/UserIdCard";
import VendorPayment from "./pages/VendorPayment";
import VendorIdCard from "./pages/VendorIdCard";
import WaitingApproval from "./pages/WaitingApproval";
import { auth, db } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import Profile from "./pages/Profile";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

// ✅ HARDCODED ADMIN EMAILS — any email in this list is ALWAYS admin.
const ADMIN_EMAILS = [
  "kishorshetty0024@gmail.com",
  "admin@connect.com",
  "support.connectcc@gmail.com"
];

const isAdminEmail = (email) =>
  !!email && ADMIN_EMAILS.map(e => e.toLowerCase()).includes(email.toLowerCase());

export default function App() {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);            // "admin" | "vendor" | "user" | null
  const [status, setStatus] = useState(null);        // user-status OR vendor approvalStatus
  const [vendorData, setVendorData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [lang, setLang] = useState(localStorage.getItem("lang") || "en");

  useEffect(() => {
    localStorage.setItem("lang", lang);
  }, [lang]);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);

        // ✅ ADMIN OVERRIDE
        if (isAdminEmail(firebaseUser.email)) {
          setRole("admin");
          setStatus("completed");
          setVendorData(null);
          setLoading(false);
          return;
        }

        await resolveRole(firebaseUser.uid);
      } else {
        setUser(null);
        setRole(null);
        setStatus(null);
        setVendorData(null);
        setLoading(false);
      }
    });
    return () => unsubscribe();
  }, []);

  // 🔥 RESOLVE ROLE — checks vendors FIRST (more specific), then users.
  // Never silently defaults to "user" on error — that was causing vendors
  // to be routed to the user dashboard when Firestore reads were slow or
  // permission-denied.
  const resolveRole = async (uid) => {
    setLoading(true);
    try {
      // 1. VENDOR CHECK
      const vendorDoc = await getDoc(doc(db, "vendors", uid));
      if (vendorDoc.exists()) {
        const data = vendorDoc.data();
        setRole("vendor");
        setStatus(data.approvalStatus || "pending");
        setVendorData(data);
        setLoading(false);
        return;
      }

      // 2. USER CHECK
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        const data = userDoc.data();
        const resolvedRole = (data.role || "user").toString().trim().toLowerCase();
        setRole(resolvedRole);
        setStatus(data.status || "registered");
        setVendorData(null);
        setLoading(false);
        return;
      }

      // 3. NO RECORD — default to regular user (new registration path).
      setRole("user");
      setStatus("registered");
      setVendorData(null);
      setLoading(false);
    } catch (error) {
      console.error("resolveRole failed:", error);
      // ❌ DO NOT default to role=user on error, that's the bug.
      // Keep role=null so the app shows Loading / error instead of
      // mis-routing a vendor to the user dashboard.
      setRole(null);
      setStatus(null);
      setVendorData(null);
      setLoading(false);
      alert("Login problem: " + (error?.code || "") + " " + (error?.message || error));
    }
  };

  // 🔥 VENDOR ROUTE — single source of truth for vendor redirects.
  const getVendorRoute = () => {
    if (!vendorData) return <div style={{ textAlign: "center", marginTop: 80 }}>Loading vendor…</div>;

    if (vendorData.paymentStatus !== "submitted" && vendorData.paymentStatus !== "approved") {
      return <Navigate to="/vendor-payment" replace />;
    }
    if (vendorData.approvalStatus !== "approved") {
      return <Navigate to="/waiting-approval" replace />;
    }
    return <Navigate to="/vendor-dashboard" replace />;
  };

  // 🔥 ROOT ROUTE — decides where every signed-in user lands.
  const getRootRoute = () => {
    if (!user) return <Login setUser={setUser} lang={lang} setLang={setLang} />;
    if (role === "admin")  return <Navigate to="/admin" replace />;
    if (role === "vendor") return getVendorRoute();
    if (role === "user") {
      return status === "completed"
        ? <Navigate to="/dashboard" replace />
        : <Navigate to="/user-idcard" replace />;
    }
    // role still unknown — keep Loading rather than mis-route.
    return <div style={{ textAlign: "center", marginTop: 80 }}>Loading…</div>;
  };

  // 🔥 GUARDED ROUTES — make sure each page is reachable only by the
  // correct role/state. Vendors can no longer hit /dashboard, etc.
  const userOnly = (element) => {
    if (!user) return <Navigate to="/" replace />;
    if (role === "admin")  return <Navigate to="/admin" replace />;
    if (role === "vendor") return getVendorRoute();
    if (role !== "user")   return <div style={{ textAlign: "center", marginTop: 80 }}>Loading…</div>;
    return element;
  };

  const vendorOnly = (element) => {
    if (!user) return <Navigate to="/" replace />;
    if (role === "admin")  return <Navigate to="/admin" replace />;
    if (role !== "vendor") return <Navigate to="/" replace />;
    return element;
  };

  const adminOnly = (element) => {
    if (!user) return <Navigate to="/" replace />;
    if (role !== "admin") return <Navigate to="/" replace />;
    return element;
  };

  return (
    <BrowserRouter>
      <div style={{ padding: 0 }}>
        {loading ? (
          <div style={{ textAlign: "center", marginTop: 80, fontSize: 18 }}>
            Loading...
          </div>
        ) : (
          <Routes>
            <Route path="/" element={getRootRoute()} />

            <Route path="/register"        element={<Register lang={lang} setLang={setLang} />} />
            <Route path="/vendor-register" element={<VendorRegister lang={lang} setLang={setLang} />} />
            <Route path="/otp"             element={<OtpVerification />} />

            {/* USER-ONLY */}
            <Route path="/dashboard"   element={userOnly(<Dashboard setUser={setUser} lang={lang} setLang={setLang} />)} />
            <Route path="/user-idcard" element={userOnly(<UserIdCard />)} />
            <Route path="/profile"     element={userOnly(<Profile />)} />
            <Route path="/premium"     element={userOnly(<Premium />)} />

            {/* VENDOR-ONLY */}
            <Route path="/vendor-dashboard"  element={vendorOnly(<VendorDashboard setUser={setUser} lang={lang} setLang={setLang} />)} />
            <Route path="/vendor-payment"    element={vendorOnly(<VendorPayment />)} />
            <Route path="/waiting-approval"  element={vendorOnly(<WaitingApproval />)} />
            <Route path="/vendor-idcard"     element={vendorOnly(<VendorIdCard />)} />

            {/* ADMIN-ONLY */}
            <Route path="/admin" element={adminOnly(<AdminDashboard setUser={setUser} />)} />

            {/* CATCH-ALL */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        )}
      </div>
    </BrowserRouter>
  );
}